/**
 * HVM Cart — core (pure logic, no DOM).
 * Namespace: window.HVMCart
 * Loaded before hvm-cart-drawer.js and hvm-cart-modules.js (defer order).
 */
(() => {
  if (window.HVMCart) return;

  const HVMCart = {};
  window.HVMCart = HVMCart;

  /* ------------------------------------------------------------------ *
   * Money — "$926" / "$1,026.50" (no currency suffix, no trailing .00)
   * ------------------------------------------------------------------ */
  HVMCart.formatMoney = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    const hasDecimals = Math.round(amount * 100) % 100 !== 0;
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    });
    return `$${formatted}`;
  };

  /* ------------------------------------------------------------------ *
   * Errors
   * ------------------------------------------------------------------ */
  class CartApiError extends Error {
    constructor(message, { status, payload } = {}) {
      super(message);
      this.name = 'CartApiError';
      this.status = status || 0;
      this.payload = payload || null;
    }
    get isNetwork() {
      return this.status === 0;
    }
  }
  HVMCart.CartApiError = CartApiError;

  /* ------------------------------------------------------------------ *
   * CartAPI — thin wrapper over Shopify Cart AJAX API.
   * Every mutation asks for `sections` so the UI updates with zero
   * extra round-trips. Lines are addressed by KEY, never by index.
   * ------------------------------------------------------------------ */
  const REQUEST_TIMEOUT_MS = 10000;

  class CartAPI {
    constructor(routes, sectionIds) {
      this.routes = routes;
      this.sectionIds = sectionIds;
    }

    async _request(url, body) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      let response;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
      } catch (err) {
        throw new CartApiError('network', { status: 0 });
      } finally {
        clearTimeout(timer);
      }

      let payload = null;
      try {
        payload = await response.json();
      } catch (err) {
        /* non-JSON body (5xx html) */
      }

      // QA diagnostics: set localStorage.hvmCartDebug = '1' to trace every
      // cart API request/response pair in the console.
      try {
        if (window.localStorage && window.localStorage.getItem('hvmCartDebug')) {
          console.log('[HVM cart]', url, { request: body, status: response.status, response: payload });
        }
      } catch (err) {
        /* storage unavailable */
      }

      if (!response.ok || (payload && payload.status && payload.status !== 200)) {
        throw new CartApiError((payload && (payload.description || payload.message)) || 'cart_error', {
          status: (payload && payload.status) || response.status,
          payload,
        });
      }
      return payload;
    }

    _withSections(body) {
      return Object.assign({}, body, {
        sections: this.sectionIds.join(','),
        sections_url: window.location.pathname,
      });
    }

    add(items) {
      return this._request(`${this.routes.cart_add_url}.js`, this._withSections({ items }));
    }

    changeByKey(key, quantity, properties) {
      const body = { id: key, quantity };
      if (properties) body.properties = properties;
      return this._request(`${this.routes.cart_change_url}.js`, this._withSections(body));
    }

    update(payload) {
      return this._request(`${this.routes.cart_update_url}.js`, this._withSections(payload));
    }

    async fetchCart() {
      const response = await fetch(`${this.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new CartApiError('network', { status: response.status });
      return response.json();
    }

    async fetchSectionHTML(sectionId) {
      const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
      if (!response.ok) throw new CartApiError('network', { status: response.status });
      return response.text();
    }
  }
  HVMCart.CartAPI = CartAPI;

  /* ------------------------------------------------------------------ *
   * MutationQueue — serializes cart mutations (concurrent /cart/change.js
   * calls can clobber each other server-side). Promise chain, not a
   * semaphore: no polling, no permanent lock if a task throws.
   * ------------------------------------------------------------------ */
  class MutationQueue {
    constructor() {
      this._chain = Promise.resolve();
      this._pending = 0;
    }

    get pending() {
      return this._pending;
    }

    enqueue(task) {
      this._pending += 1;
      const run = this._chain.catch(() => {}).then(task);
      this._chain = run.catch(() => {}).finally(() => {
        this._pending -= 1;
      });
      return run;
    }

    async idle() {
      while (this._pending > 0) {
        await this._chain.catch(() => {});
      }
    }
  }
  HVMCart.MutationQueue = MutationQueue;

  /* ------------------------------------------------------------------ *
   * LineDebouncer — one timer PER line key so edits on two lines within
   * the debounce window never cancel each other. Removals flush.
   * ------------------------------------------------------------------ */
  class LineDebouncer {
    constructor(delay = 300) {
      this.delay = delay;
      this._entries = new Map();
    }

    schedule(key, fn) {
      this.cancel(key);
      const timer = setTimeout(() => {
        this._entries.delete(key);
        fn();
      }, this.delay);
      this._entries.set(key, { timer, fn });
    }

    cancel(key) {
      const entry = this._entries.get(key);
      if (entry) {
        clearTimeout(entry.timer);
        this._entries.delete(key);
      }
    }

    flush(key) {
      if (key !== undefined) {
        const entry = this._entries.get(key);
        if (entry) {
          clearTimeout(entry.timer);
          this._entries.delete(key);
          entry.fn();
        }
        return;
      }
      const entries = [...this._entries.values()];
      this._entries.clear();
      entries.forEach((entry) => {
        clearTimeout(entry.timer);
        entry.fn();
      });
    }

    get size() {
      return this._entries.size;
    }
  }
  HVMCart.LineDebouncer = LineDebouncer;

  /* ------------------------------------------------------------------ *
   * State derivation. A line is a gift when it carries the `_hvm_gift`
   * property (or the legacy `_is_promo_gift` marker from the retired
   * promo engine). Gifts never count toward the tier base. When the
   * merchant disables `tiers.countSubscriptions`, lines purchased on a
   * selling plan are excluded from that base too.
   * ------------------------------------------------------------------ */
  // `_free_product` es la marca que dejó la app de regalos (Kite, retirada
  // sep 2026): una línea así ya no tiene descuento, queda cobrada y se limpia.
  const GIFT_PROPS = ['_hvm_gift', '_is_promo_gift', '_free_product'];

  HVMCart.isGiftItem = (item) => {
    const props = item.properties || {};
    return GIFT_PROPS.some((name) => props[name]);
  };

  /** Unidades de la línea que quedaron en $0 (entera o parcialmente
   *  descontada por el BXGY). */
  HVMCart.freeUnits = (item) => {
    if (item.final_line_price === 0) return item.quantity;
    const unit = Number(item.original_price) || 0;
    const discount = Number(item.line_level_total_discount) || 0;
    if (!unit || !discount) return 0;
    return Math.floor(discount / unit);
  };

  HVMCart.derive = (cart, config) => {
    // El estado embebido serializa `item.properties | json` como lista de
    // pares ([["_hvm_gift","promo"]]); la Cart API lo manda como objeto.
    // Sin normalizar, al cargar la página los regalos no se reconocen.
    const items = (cart.items || []).map((item) =>
      Array.isArray(item.properties) ? Object.assign({}, item, { properties: Object.fromEntries(item.properties) }) : item
    );
    // Subscriptions count toward thresholds unless the merchant opted out
    // (config.tiers.countSubscriptions === false); undefined keeps legacy behaviour.
    const countSubs = !(config && config.tiers && config.tiers.countSubscriptions === false);
    const giftLines = items.filter((item) => HVMCart.isGiftItem(item));
    const paidSubtotal = items.reduce((sum, item) => {
      if (HVMCart.isGiftItem(item)) return sum;
      if (!countSubs && item.selling_plan_allocation) return sum;
      return sum + item.final_line_price;
    }, 0);
    const tiers = (config && config.tiers && config.tiers.gifts) || [];
    const unlocked = tiers.filter((tier) => paidSubtotal >= tier.threshold);

    // Promo por unidades: misma regla que el gate server-side (unidades
    // pagadas, sin suscripciones, dentro de la colección elegible si hay).
    const promo = (config && config.promo) || {};
    const eligible = Array.isArray(promo.eligibleProductIds)
      ? new Set(promo.eligibleProductIds.map(String))
      : null;
    const promoUnits = items.reduce((sum, item) => {
      if (HVMCart.isGiftItem(item)) return sum;
      if (item.selling_plan_allocation) return sum;
      if (eligible && !eligible.has(String(item.product_id))) return sum;
      return sum + item.quantity;
    }, 0);
    // Escalones compra:regalo ascendentes (misma lectura que el Liquid); sin
    // escalones = uno solo con minUnits/giftQuantity. El regalo ganado es el
    // del escalón más alto alcanzado, sin repetirse.
    let steps = Array.isArray(promo.steps)
      ? promo.steps
          .map((step) => ({ units: Number(step.units) || 0, gift: Number(step.gift) || 0 }))
          .filter((step) => step.units > 0 && step.gift > 0)
      : [];
    if (!steps.length) {
      steps = [{ units: Math.max(1, Number(promo.minUnits) || 1), gift: Math.max(1, Number(promo.giftQuantity) || 1) }];
    }
    const promoMinUnits = steps[0].units;
    const promoQualified = promoUnits >= promoMinUnits;
    const promoGiftQuantity = steps.reduce((qty, step) => (promoUnits >= step.units ? step.gift : qty), 0);

    return {
      items,
      giftLines,
      paidSubtotal,
      unlockedTiers: unlocked,
      promoUnits,
      promoMinUnits,
      promoQualified,
      promoGiftQuantity,
    };
  };

  /* ------------------------------------------------------------------ *
   * Store — canonical cart state = last API response (or the JSON the
   * section embeds server-side). Diffs are computed against the PREVIOUS
   * store snapshot, never against the already-updated DOM.
   * ------------------------------------------------------------------ */
  class Store {
    constructor(config) {
      this.config = config;
      this.cart = null;
      this.derived = null;
    }

    commit(cart) {
      const prevCart = this.cart;
      const prevDerived = this.derived;
      this.cart = cart;
      this.derived = HVMCart.derive(cart, this.config);

      const prevKeys = new Set(((prevCart && prevCart.items) || []).map((item) => item.key));
      const nextKeys = new Set(this.derived.items.map((item) => item.key));

      return {
        prevCart,
        prevDerived,
        cart,
        derived: this.derived,
        diff: {
          itemCountChanged: !prevCart || prevCart.item_count !== cart.item_count,
          totalChanged: !prevCart || prevCart.total_price !== cart.total_price,
          addedKeys: [...nextKeys].filter((key) => !prevKeys.has(key)),
          removedKeys: [...prevKeys].filter((key) => !nextKeys.has(key)),
        },
      };
    }

    findItem(key) {
      return this.derived ? this.derived.items.find((item) => item.key === key) : undefined;
    }
  }
  HVMCart.Store = Store;

  /* ------------------------------------------------------------------ *
   * RulesEngine — business invariants enforced after every commit.
   * Corrections only ever REMOVE or SHRINK gift lines, so the paid
   * subtotal is invariant under corrections and the engine reaches a
   * fixed point in one pass. Max two passes as a hard stop.
   *
   * R1 gift whose tier is no longer met      -> remove (+ recovery toast)
   * R1' promo gift below the unit minimum    -> remove (+ recovery toast)
   * R2 gift line that is not free            -> remove (discount missing)
   * R3 gift line above its allowed quantity  -> clamp (1 per tier, N per promo)
   * R0 promo mode suspends R1 for tier gifts (promo gift governs), R2/R3
   *    always run: a non-free "gift" is never acceptable.
   * ------------------------------------------------------------------ */
  HVMCart.evaluateRules = (derived, config) => {
    const corrections = [];
    const tiersEnabled = config.tiers && config.tiers.enabled;
    const promoActive = config.promo && config.promo.active;

    derived.items.forEach((item) => {
      if (!HVMCart.isGiftItem(item)) return;
      const props = item.properties || {};
      let giftSource = props._hvm_gift || 'tier';
      if (!props._hvm_gift && !props._is_promo_gift && props._free_product) giftSource = 'app';

      // Línea heredada de la app de regalos (retirada): solo R2/R3, una
      // línea de app cobrada se elimina.

      let allowedQty = 1;
      if (giftSource === 'promo') {
        const promoGiftId = config.promo && config.promo.giftVariantId;
        if (!promoActive || String(promoGiftId) !== String(item.variant_id)) {
          corrections.push({ type: 'remove', key: item.key, reason: 'promo_over', item });
          return;
        }
        // Mismo orden que en tiers: al bajar de las unidades mínimas el BXGY
        // deja de aplicar y la línea queda COBRADA; diagnosticar primero la
        // pérdida de la promo para dar el mensaje correcto.
        if (!derived.promoQualified) {
          corrections.push({
            type: 'remove',
            key: item.key,
            reason: 'promo_lost',
            missingUnits: derived.promoMinUnits - derived.promoUnits,
            item,
          });
          return;
        }
        // Al bajar de escalón (3 → 2 frascos) el regalo se recorta al del
        // escalón vigente; el BXGY del escalón alto deja de aplicar.
        allowedQty = Math.max(1, derived.promoGiftQuantity);
        // Al bajar de escalón Shopify puede partir el regalo en varias líneas
        // marcadas (una a $0 y otra cobrada, cada una dentro del tope): el
        // tope se mide sobre todas las unidades marcadas del variant y el
        // excedente sale de las líneas cobradas.
        if (item.final_line_price > 0) {
          const flaggedUnits = derived.items
            .filter(
              (other) =>
                String(other.variant_id) === String(item.variant_id) &&
                !other.selling_plan_allocation &&
                (other.properties || {})._hvm_gift === 'promo'
            )
            .reduce((sum, other) => sum + other.quantity, 0);
          const excess = flaggedUnits - allowedQty;
          if (excess > 0) {
            const keep = Math.max(0, item.quantity - excess);
            corrections.push(
              keep > 0
                ? { type: 'clamp', key: item.key, quantity: keep, reason: 'gift_qty', item }
                : { type: 'remove', key: item.key, reason: 'gift_qty', item }
            );
            return;
          }
        }
        // El BXGY reparte el $0 entre TODAS las líneas del variant (si el
        // shopper también compró el producto del regalo, el $0 puede caer en
        // su línea pagada y la marcada quedar cobrada). El total es correcto:
        // el regalo se considera aplicado si el variant suma las unidades
        // gratis prometidas; se recorta por línea pero no se retira.
        if (item.final_line_price > 0) {
          const variantFree = derived.items
            .filter((other) => String(other.variant_id) === String(item.variant_id) && !other.selling_plan_allocation)
            .reduce((sum, other) => sum + HVMCart.freeUnits(other), 0);
          if (variantFree >= allowedQty) {
            if (item.quantity > allowedQty) {
              corrections.push({ type: 'clamp', key: item.key, quantity: allowedQty, reason: 'gift_qty', item });
            }
            return;
          }
        }
      }

      if (giftSource === 'tier') {
        if (promoActive || !tiersEnabled) {
          corrections.push({ type: 'remove', key: item.key, reason: 'tiers_suspended', item });
          return;
        }
        const tier = (config.tiers.gifts || []).find(
          (candidate) => String(candidate.variantId) === String(item.variant_id)
        );
        // A gift line whose variant is no longer a configured tier gift is an
        // orphan (merchant swapped the gift product): remove it.
        if (!tier) {
          corrections.push({ type: 'remove', key: item.key, reason: 'tiers_suspended', item });
          return;
        }
        // Order matters: when the subtotal drops below the threshold the
        // automatic discount stops applying and the line becomes PAID, so a
        // lost tier must be diagnosed before "not free" or the shopper gets
        // the wrong message on the reverse path.
        if (tier && derived.paidSubtotal < tier.threshold) {
          corrections.push({
            type: 'remove',
            key: item.key,
            reason: 'tier_lost',
            missing: tier.threshold - derived.paidSubtotal,
            item,
          });
          return;
        }
      }

      if (item.final_line_price > 0) {
        corrections.push({ type: 'remove', key: item.key, reason: 'not_free', item });
        return;
      }
      if (item.quantity > allowedQty) {
        corrections.push({ type: 'clamp', key: item.key, quantity: allowedQty, reason: 'gift_qty', item });
      }
    });

    return corrections;
  };

})();
